import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { submitProjectInquiry } from '../utils/formSubmission';
import BotPrevention from './BotPrevention';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Modal Container
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 10px;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: ${slideIn} 0.4s ease;
  overflow: hidden;
  position: relative;
  margin: auto;
  
  @media (max-width: 768px) {
    margin: 1rem auto;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  transition: color 0.2s ease;
  z-index: 10;
  
  &:hover {
    color: #333;
  }
`;

const ModalHeader = styled.div`
  padding: 1.75rem 2rem 0;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 1.6rem;
  color: #333;
  margin-bottom: 0.25rem;
`;

const StepIndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
`;

const StepLine = styled.div`
  height: 3px;
  background-color: #eee;
  margin: 0 0.5rem;
  flex-grow: 1;
  max-width: 60px;
  border-radius: 3px;
  transition: background-color 0.3s ease;
`;

const ActiveStepLine = styled(StepLine)`
  background-color: var(--primary-color);
`;

const ModalBody = styled.div`
  padding: 0 2rem 2rem;
`;

const FormStep = styled.div`
  animation: ${fadeIn} 0.4s ease;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #444;
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
  }
`;

const TextAreaField = styled.textarea`
  width: 100%;
  padding: 0.9rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s ease;
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const RadioLabel = styled.label<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  background-color: ${props => props.selected ? 'rgba(255, 107, 0, 0.1)' : '#f5f5f5'};
  border: 1px solid ${props => props.selected ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.selected ? 'var(--primary-color)' : '#444'};
  font-weight: ${props => props.selected ? '500' : 'normal'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.selected ? 'rgba(255, 107, 0, 0.1)' : '#eee'};
  }
  
  input {
    position: absolute;
    opacity: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const ButtonBase = styled.button`
  padding: 0.8rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const PrimaryButton = styled(ButtonBase)`
  background-color: var(--primary-color);
  color: white;
  border: none;
  
  &:hover {
    background-color: #e05e00;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(ButtonBase)`
  background-color: white;
  color: #444;
  border: 1px solid #ddd;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const StepDescription = styled.p`
  text-align: center;
  color: #444;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  
  svg {
    width: 70px;
    height: 70px;
    color: var(--primary-color);
    margin-bottom: 1rem;
  }
  
  h4 {
    font-size: 1.6rem;
    margin-bottom: 1rem;
    color: #333;
  }
  
  p {
    color: #444;
    margin-bottom: 2rem;
  }
`;

// Success Icon
const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HoneypotInput = styled.input`
  display: none;
`;

interface ProjectData {
  name: string;
  email: string;
  companyName: string;
  projectType: string;
  timeframe: string;
  budget: string;
  description: string;
}

interface LearnMoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceName?: string;
  /** Full header text; falls back to the "Learn More About …" template. */
  title?: string;
}

const LearnMoreModal: React.FC<LearnMoreModalProps> = ({ isOpen, onClose, serviceName = "Our Services", title }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [projectData, setProjectData] = useState<ProjectData>({
    name: '',
    email: '',
    companyName: '',
    projectType: '',
    timeframe: '',
    budget: '',
    description: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isHumanVerified, setIsHumanVerified] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  const handleValidationChange = (isValid: boolean) => {
    setIsHumanVerified(isValid);
  };
  
  const scrollToTop = () => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  };
  
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);
  
  useEffect(() => {
    if (isOpen && inputRef.current && !isSubmitted) {
      inputRef.current.focus();
      
      // Add slight delay to ensure input is visible after keyboard appears
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [currentStep, isOpen, isSubmitted]);
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setIsSubmitted(false);
      setSubmitError(null);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProjectData(prev => ({ ...prev, [name]: value }));
  };
  
  const setRadioValue = (name: string, value: string) => {
    setProjectData(prev => ({ ...prev, [name]: value }));
  };
  
  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };
  
  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };
  
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (honeypot) {
      console.log('Bot detected via honeypot');
      setIsSubmitted(true);
      return;
    }
    
    if (currentStep === 7 && !isHumanVerified) {
      setSubmitError('Human verification failed. Please try again.');
      return;
    }
    
    if (currentStep === 7 && projectData.description) {
      try {
        setIsSubmitting(true);
        setSubmitError(null);
        
        const result = await submitProjectInquiry(projectData);
        
        if (result.success) {
          console.log('Form submitted successfully:', result);
          setIsSubmitted(true);
        } else {
          setSubmitError(result.message);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitError('An unexpected error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } 
    else if (
      (currentStep === 1 && projectData.name) ||
      (currentStep === 2 && projectData.email) ||
      (currentStep === 3 && projectData.companyName) ||
      (currentStep === 4 && projectData.projectType) ||
      (currentStep === 5 && projectData.timeframe) ||
      (currentStep === 6 && projectData.budget)
    ) {
      nextStep();
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                Let's start with some basic information
              </StepDescription>
              <InputLabel htmlFor="name">Your Name</InputLabel>
              <InputField
                type="text"
                id="name"
                name="name"
                value={projectData.name}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLInputElement>}
                placeholder="John Doe"
                required
              />
              <ButtonContainer>
                <SecondaryButton type="button" onClick={onClose}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!projectData.name}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 2:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                How can we contact you?
              </StepDescription>
              <InputLabel htmlFor="email">Email Address</InputLabel>
              <InputField
                type="email"
                id="email"
                name="email"
                value={projectData.email}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLInputElement>}
                placeholder="your.email@example.com"
                required
              />
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!projectData.email}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 3:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                Tell us about your company
              </StepDescription>
              <InputLabel htmlFor="companyName">Company Name</InputLabel>
              <InputField
                type="text"
                id="companyName"
                name="companyName"
                value={projectData.companyName}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLInputElement>}
                placeholder="Your Company Ltd."
                required
              />
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!projectData.companyName}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 4:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                What type of project are you interested in?
              </StepDescription>
              <InputLabel>Project Type</InputLabel>
              <RadioGroup>
                <RadioLabel 
                  selected={projectData.projectType === 'Website Development'}
                  onClick={() => setRadioValue('projectType', 'Website Development')}
                >
                  <input 
                    type="radio" 
                    name="projectType" 
                    value="Website Development" 
                    checked={projectData.projectType === 'Website Development'} 
                    onChange={handleInputChange}
                  />
                  Website Development
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.projectType === 'Mobile App'}
                  onClick={() => setRadioValue('projectType', 'Mobile App')}
                >
                  <input 
                    type="radio" 
                    name="projectType" 
                    value="Mobile App" 
                    checked={projectData.projectType === 'Mobile App'} 
                    onChange={handleInputChange}
                  />
                  Mobile App
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.projectType === 'Custom Software'}
                  onClick={() => setRadioValue('projectType', 'Custom Software')}
                >
                  <input 
                    type="radio" 
                    name="projectType" 
                    value="Custom Software" 
                    checked={projectData.projectType === 'Custom Software'} 
                    onChange={handleInputChange}
                  />
                  Custom Software
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.projectType === 'Integration'}
                  onClick={() => setRadioValue('projectType', 'Integration')}
                >
                  <input 
                    type="radio" 
                    name="projectType" 
                    value="Integration" 
                    checked={projectData.projectType === 'Integration'} 
                    onChange={handleInputChange}
                  />
                  Integration
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.projectType === 'Consulting'}
                  onClick={() => setRadioValue('projectType', 'Consulting')}
                >
                  <input 
                    type="radio" 
                    name="projectType" 
                    value="Consulting" 
                    checked={projectData.projectType === 'Consulting'} 
                    onChange={handleInputChange}
                  />
                  Consulting
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.projectType === 'Other'}
                  onClick={() => setRadioValue('projectType', 'Other')}
                >
                  <input 
                    type="radio" 
                    name="projectType" 
                    value="Other" 
                    checked={projectData.projectType === 'Other'} 
                    onChange={handleInputChange}
                  />
                  Other
                </RadioLabel>
              </RadioGroup>
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!projectData.projectType}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 5:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                What's your expected timeframe?
              </StepDescription>
              <InputLabel>Timeframe</InputLabel>
              <RadioGroup>
                <RadioLabel 
                  selected={projectData.timeframe === 'Immediate (1-2 weeks)'}
                  onClick={() => setRadioValue('timeframe', 'Immediate (1-2 weeks)')}
                >
                  <input 
                    type="radio" 
                    name="timeframe" 
                    value="Immediate (1-2 weeks)" 
                    checked={projectData.timeframe === 'Immediate (1-2 weeks)'} 
                    onChange={handleInputChange}
                  />
                  Immediate (1-2 weeks)
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.timeframe === 'Short-term (1-2 months)'}
                  onClick={() => setRadioValue('timeframe', 'Short-term (1-2 months)')}
                >
                  <input 
                    type="radio" 
                    name="timeframe" 
                    value="Short-term (1-2 months)" 
                    checked={projectData.timeframe === 'Short-term (1-2 months)'} 
                    onChange={handleInputChange}
                  />
                  Short-term (1-2 months)
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.timeframe === 'Medium-term (3-6 months)'}
                  onClick={() => setRadioValue('timeframe', 'Medium-term (3-6 months)')}
                >
                  <input 
                    type="radio" 
                    name="timeframe" 
                    value="Medium-term (3-6 months)" 
                    checked={projectData.timeframe === 'Medium-term (3-6 months)'} 
                    onChange={handleInputChange}
                  />
                  Medium-term (3-6 months)
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.timeframe === 'Long-term (6+ months)'}
                  onClick={() => setRadioValue('timeframe', 'Long-term (6+ months)')}
                >
                  <input 
                    type="radio" 
                    name="timeframe" 
                    value="Long-term (6+ months)" 
                    checked={projectData.timeframe === 'Long-term (6+ months)'} 
                    onChange={handleInputChange}
                  />
                  Long-term (6+ months)
                </RadioLabel>
              </RadioGroup>
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!projectData.timeframe}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 6:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                What's your approximate budget?
              </StepDescription>
              <InputLabel>Budget Range</InputLabel>
              <RadioGroup>
                <RadioLabel 
                  selected={projectData.budget === 'Less than $5,000'}
                  onClick={() => setRadioValue('budget', 'Less than $5,000')}
                >
                  <input 
                    type="radio" 
                    name="budget" 
                    value="Less than $5,000" 
                    checked={projectData.budget === 'Less than $5,000'} 
                    onChange={handleInputChange}
                  />
                  Less than $5,000
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.budget === '$5,000 - $10,000'}
                  onClick={() => setRadioValue('budget', '$5,000 - $10,000')}
                >
                  <input 
                    type="radio" 
                    name="budget" 
                    value="$5,000 - $10,000" 
                    checked={projectData.budget === '$5,000 - $10,000'} 
                    onChange={handleInputChange}
                  />
                  $5,000 - $10,000
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.budget === '$10,000 - $25,000'}
                  onClick={() => setRadioValue('budget', '$10,000 - $25,000')}
                >
                  <input 
                    type="radio" 
                    name="budget" 
                    value="$10,000 - $25,000" 
                    checked={projectData.budget === '$10,000 - $25,000'} 
                    onChange={handleInputChange}
                  />
                  $10,000 - $25,000
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.budget === '$25,000 - $50,000'}
                  onClick={() => setRadioValue('budget', '$25,000 - $50,000')}
                >
                  <input 
                    type="radio" 
                    name="budget" 
                    value="$25,000 - $50,000" 
                    checked={projectData.budget === '$25,000 - $50,000'} 
                    onChange={handleInputChange}
                  />
                  $25,000 - $50,000
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.budget === '$50,000+'}
                  onClick={() => setRadioValue('budget', '$50,000+')}
                >
                  <input 
                    type="radio" 
                    name="budget" 
                    value="$50,000+" 
                    checked={projectData.budget === '$50,000+'} 
                    onChange={handleInputChange}
                  />
                  $50,000+
                </RadioLabel>
                <RadioLabel 
                  selected={projectData.budget === 'Not sure yet'}
                  onClick={() => setRadioValue('budget', 'Not sure yet')}
                >
                  <input 
                    type="radio" 
                    name="budget" 
                    value="Not sure yet" 
                    checked={projectData.budget === 'Not sure yet'} 
                    onChange={handleInputChange}
                  />
                  Not sure yet
                </RadioLabel>
              </RadioGroup>
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!projectData.budget}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 7:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                Tell us more about your project
              </StepDescription>
              <InputLabel htmlFor="description">Project Description</InputLabel>
              <TextAreaField
                id="description"
                name="description"
                value={projectData.description}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                placeholder="Please describe your project goals, challenges, and any specific requirements..."
                required
              />
              
              <HoneypotInput
                type="text"
                name="website_field"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
              />
              
              <BotPrevention onValidationChange={handleValidationChange} formId="learnMore" />
              
              {submitError && (
                <div style={{ color: 'red', marginTop: '10px', fontSize: '0.9rem' }}>
                  {submitError}
                </div>
              )}
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!projectData.description || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      default:
        return null;
    }
  };
  
  const renderStepIndicators = () => {
    const totalSteps = 7;
    const indicators = [];
    
    for (let i = 1; i <= totalSteps; i++) {
      if (i <= currentStep) {
        indicators.push(<ActiveStepLine key={i} />);
      } else {
        indicators.push(<StepLine key={i} />);
      }
    }
    
    return indicators;
  };
  
  return (
    <ModalOverlay onKeyDown={handleKeyDown} onClick={onClose}>
      <ModalContent ref={modalRef} onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        
        <ModalHeader>
          <ModalTitle>{title ?? `Learn More About ${serviceName}`}</ModalTitle>
          {!isSubmitted && (
            <StepIndicatorContainer>
              {renderStepIndicators()}
            </StepIndicatorContainer>
          )}
        </ModalHeader>
        
        <ModalBody>
          {!isSubmitted ? (
            renderStep()
          ) : (
            <SuccessMessage>
              <SuccessIcon />
              <h4>Request Submitted!</h4>
              <p>Thank you for your interest. Our team will review your project details and get back to you within 1-2 business days.</p>
              <PrimaryButton onClick={onClose}>
                Close
              </PrimaryButton>
            </SuccessMessage>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default LearnMoreModal; 